import { Form, FormItemProps, Input, Radio, Space } from "antd";
import { RadioButtonProps } from "antd/es/radio/radioButton";
import { CheckboxOptionType } from "antd/lib";
import { useEffect, useState } from "react";
import { compareCheckboxOptionsDeep } from "../../../utils/functions";
import style from "./FormItem.module.scss";

type RadioFormItemProps<T> = FormItemProps<T> &
  Omit<RadioButtonProps, "name"> & {
    testId?: string;
    direction?: "horizontal" | "vertical";
    options: Array<CheckboxOptionType> | undefined;
    isLoadingOptions?: boolean;
    textOnly?: boolean;
    valuePropName?: string;
  };

export const RadioFormItem = <T extends object>({
  id,
  name,
  label,
  rules,
  testId,
  className,
  required,
  direction = "horizontal",
  options,
  isLoadingOptions,
  value,
  textOnly,
  valuePropName,
  ...props
}: RadioFormItemProps<T>) => {
  const [radioOptions, setRadioOptions] = useState<CheckboxOptionType[]>([]);
  const [prevRadioOptions, setPrevRadioOptions] = useState<
    Array<CheckboxOptionType> | undefined
  >([]);

  // NOTE: Prevent re-renders by deeply comparing fetched radio options, as React treats different array references as new items.
  useEffect(() => {
    if (options && prevRadioOptions) {
      const optionsChanged = !compareCheckboxOptionsDeep(
        options,
        prevRadioOptions
      );

      if (optionsChanged) {
        setRadioOptions(options);
      }
    }

    if (options && prevRadioOptions === undefined) {
      setRadioOptions(options);
    }

    setPrevRadioOptions(options);
  }, [options]);

  return (
    <Form.Item
      id={id}
      name={name}
      label={label}
      rules={rules}
      className={`${style.formItem} ${className}`}
      data-testid={testId}
      required={required}
      valuePropName={valuePropName}
    >
      {textOnly && value !== undefined ? (
        <Input
          name={id}
          value={value}
          className={`${style.fontSize} ${style.formItemInput}`}
          disabled
        />
      ) : (
        <Radio.Group
          {...props}
          defaultValue={value !== undefined ? value : undefined}
        >
          <Space direction={direction} className={style.radioGroup}>
            {radioOptions?.map((option, index) => {
              return (
                <Radio key={index} value={option.value}>
                  {option.label}
                </Radio>
              );
            })}
          </Space>
        </Radio.Group>
      )}
    </Form.Item>
  );
};

export default RadioFormItem;
